import StreamService from "@/services/StreamService";
import router from "@/router";
import _ from "lodash";
import { sortStreams } from "../../utils";

const findStreamIndex = (streams, id) =>
  streams.findIndex((s) => s._id === id || s.id === id);

const findStream = (streams, id) => streams.find((s) => s._id === id || s.id === id);

const destDisplayName = (destination) => {
  if (!destination) return null;
  return typeof destination === "string" ? destination : destination.name || null;
};

const defaultState = () => ({
  streams: [],
  vodFiles: [],
  // Which VOD folder stream id the current `vodFiles` list belongs to.
  vodFilesFolderId: null,
  vodSubfolders: [],
  sortBy: "Sort by Oldest",
  isLoading: false,
  isLoaded: false,
  lastLoadTime: null,
});

const state = defaultState();

const mutations = {
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  GET_STREAMS(state, streams) {
    state.streams = streams.sort(function (a, b) {
      return new Date(b.creationTime) - new Date(a.creationTime);
    });
    state.isLoaded = true;
    state.isLoading = false;
    state.lastLoadTime = Date.now();
  },
  GET_VOD_FILES(state, filesOrPayload) {
    const files = Array.isArray(filesOrPayload)
      ? filesOrPayload
      : filesOrPayload?.files;
    const folderId = Array.isArray(filesOrPayload)
      ? undefined
      : filesOrPayload?.folderId;
    // A non-array payload must not wipe the open folder.
    if (!Array.isArray(files)) return;
    state.vodFiles = files.slice().sort(function (a, b) {
      return new Date(b.creationTime) - new Date(a.creationTime);
    });
    if (folderId !== undefined) {
      state.vodFilesFolderId = folderId;
    }
  },
  PREPARE_VOD_FOLDER(state, folderId) {
    if (!folderId || state.vodFilesFolderId === folderId) return;
    state.vodFiles = [];
    state.vodFilesFolderId = null;
  },
  ADD_VOD_FILE(state, file) {
    if (!file?.id) return;
    if (state.vodFiles.some((f) => f.id === file.id)) return;
    state.vodFiles = [file, ...state.vodFiles];
  },
  GET_VOD_SUBFOLDERS(state, subfolders) {
    state.vodSubfolders = subfolders;
  },
  PATCH_VOD_FILE(state, { videoId, updates }) {
    const index = state.vodFiles.findIndex((f) => f.id === videoId);
    if (index > -1) {
      state.vodFiles[index] = { ...state.vodFiles[index], ...updates };
    }
  },
  REMOVE_VOD_FILE(state, videoId) {
    state.vodFiles = state.vodFiles.filter((f) => f.id !== videoId);
  },
  SET_SORT_OPTION(state, option) {
    state.sortBy = option;
  },
  SORT_STREAMS(state, option) {
    state.streams = state.streams.sort(function (a, b) {
      if (option == "Newest first") {
        return new Date(b.creationTime) - new Date(a.creationTime);
      }
      if (option == "Oldest first") {
        return new Date(a.creationTime) - new Date(b.creationTime);
      }
    });
  },
  SEARCH_LIVE(state, searchStr) {
    state.streams = state.streams.filter((s) => {
      const search = searchStr.toLowerCase();
      let bool = s.name.toLowerCase().includes(search);
      return bool;
    });
  },
  UPDATE_STREAMS(state, updatedStreams) {
    state.streams = updatedStreams;
  },
  REMOVE_STREAM(state, streamId) {
    state.streams = state.streams.filter((s) => s._id !== streamId);
  },
  ADD_STREAM(state, stream) {
    if (!stream?._id) return;
    if (state.streams.some((item) => item._id === stream._id)) return;
    state.streams = [stream, ...state.streams];
  },
  PATCH_STREAM_PLATFORM_ENABLED(state, { streamId, platformId, enabled }) {
    const stream = state.streams.find(
      (s) => s._id === streamId || s.id === streamId,
    );
    if (!stream?.platforms) return;
    const platform = stream.platforms.find(
      (p) => p._id === platformId || p.id === platformId,
    );
    if (platform) platform.enabled = enabled;
  },
  PATCH_STREAM(state, { streamId, patch }) {
    const index = findStreamIndex(state.streams, streamId);
    if (index < 0 || !patch) return;
    const stream = state.streams[index];
    state.streams.splice(index, 1, {
      ...stream,
      ...patch,
      config: patch.config
        ? { ...(stream.config || {}), ...patch.config }
        : stream.config,
    });
  },
  INVALIDATE(state) {
    state.isLoaded = false;
  },
};

const actions = {
  async getStreams({ commit, state }, forceRefresh = false) {
    // If already loaded and not forcing refresh, skip
    if (state.isLoaded && !forceRefresh) {
      return state.streams;
    }

    // If currently loading, wait for it
    if (state.isLoading) {
      return new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!state.isLoading) {
            clearInterval(checkLoading);
            resolve(state.streams);
          }
        }, 50);
      });
    }

    commit("SET_LOADING", true);
    try {
      const streams = await StreamService.getUserStreams();
      commit("GET_STREAMS", streams);
      return streams;
    } catch (error) {
      commit("SET_LOADING", false);
      throw error;
    }
  },
  updateStreams({ commit }, updatedStreams) {
    commit("UPDATE_STREAMS", updatedStreams);
  },
  removeStream({ commit }, streamId) {
    commit("REMOVE_STREAM", streamId);
  },
  addStream({ commit }, stream) {
    commit("ADD_STREAM", stream);
  },
  patchStreamPlatformEnabled({ commit }, payload) {
    commit("PATCH_STREAM_PLATFORM_ENABLED", payload);
  },
  patchStream({ commit }, payload) {
    commit("PATCH_STREAM", payload);
  },
  // Keep TV Playout dashboard cards (and dest pickers) in sync after a dest
  // change without forcing a full getStreams refetch.
  syncPlayoutDestination({ commit, state }, { playoutId, destinationId }) {
    const playout = findStream(state.streams, playoutId);
    if (!playout) return;

    const previousDestName = destDisplayName(playout.destination);
    const newDest = destinationId ? findStream(state.streams, destinationId) : null;
    const newDestName = newDest?.name || null;

    commit("PATCH_STREAM", {
      streamId: playoutId,
      patch: { destination: newDestName },
    });

    if (previousDestName && previousDestName !== newDestName) {
      const stillLinked = state.streams.some(
        (s) =>
          s.type === "tvplayout" &&
          s._id !== playoutId &&
          destDisplayName(s.destination) === previousDestName,
      );
      const prev = state.streams.find(
        (s) => s.type === "live" && s.name === previousDestName,
      );
      if (!stillLinked && prev && prev._id !== newDest?._id) {
        commit("PATCH_STREAM", {
          streamId: prev._id,
          patch: { playoutDestination: null },
        });
      }
    }

    if (newDest) {
      commit("PATCH_STREAM", {
        streamId: newDest._id,
        patch: { playoutDestination: true },
      });
    }
  },
  invalidate({ commit }) {
    commit("INVALIDATE");
  },
  prepareVodFolder({ commit, state }, folderId) {
    if (!folderId) return;
    commit("PREPARE_VOD_FOLDER", folderId);
  },
  async getFilesInVODFolder({ commit, state }, drop) {
    if (drop === true) {
      commit("GET_VOD_FILES", { files: [], folderId: null });
      return [];
    }
    const opts = drop && typeof drop === "object" ? drop : {};
    const streamId = opts.streamId || router.currentRoute.value.params?.streamId;
    if (!streamId) return [];
    const files = await StreamService.getStreamPlaylist(streamId);
    const list = Array.isArray(files) ? files : [];
    const viewing = router.currentRoute.value.params?.streamId;
    // A background upload for another folder must not replace the open list.
    if (viewing && viewing !== streamId) return list;
    if (!Array.isArray(files)) return state.vodFiles;
    const ensureIds = Array.isArray(opts.ensureIds) ? opts.ensureIds : [];
    if (ensureIds.length) {
      const have = new Set(list.map((f) => f.id || f._id));
      const kept = state.vodFiles.filter(
        (f) => ensureIds.includes(f.id) && !have.has(f.id),
      );
      commit("GET_VOD_FILES", { files: [...kept, ...list], folderId: streamId });
    } else {
      commit("GET_VOD_FILES", { files: list, folderId: streamId });
    }
    return list;
  },
  async getVodSubfolders({ commit }, folderId) {
    if (!folderId) {
      commit("GET_VOD_SUBFOLDERS", []);
      return [];
    }
    try {
      const subfolders = await StreamService.getVodSubfolders(folderId);
      commit("GET_VOD_SUBFOLDERS", subfolders ?? []);
      return subfolders ?? [];
    } catch (err) {
      // Backend without sub-folder support (or transient failure): degrade to
      // a flat folder rather than breaking the page.
      console.error("get vod subfolders failed: ", err);
      commit("GET_VOD_SUBFOLDERS", []);
      return [];
    }
  },
  patchVodFile({ commit }, payload) {
    commit("PATCH_VOD_FILE", payload);
  },
  removeVodFile({ commit }, videoId) {
    commit("REMOVE_VOD_FILE", videoId);
  },
  async getVodFilesByVodId({ commit }, vodId) {
    if (vodId) {
      const files = await StreamService.getStreamPlaylist(vodId);
      commit("GET_VOD_FILES", { files, folderId: vodId });
    } else {
      commit("GET_VOD_FILES", { files: [], folderId: null });
    }
  },
  async setSortOption({ commit }, option) {
    commit("SET_SORT_OPTION", option);
  },
  async sortStreams({ commit }, option) {
    commit("SORT_STREAMS", option);
  },
  async searchLive({ commit }, searchStr) {
    commit("SEARCH_LIVE", searchStr);
  },
};

const getters = {
  isLoaded: (state) => state.isLoaded,
  isLoading: (state) => state.isLoading,
  getStreamById: (state) => (id) => {
    return state.streams.find((stream) => stream._id === id);
  },
  streams: (state) => {
    const streams = state.streams;
    return sortStreams(streams, state.sortBy);
  },
  multiStreams: (state) => {
    const multiStreams = state.streams.filter(
      (stream) => stream.type === "restream",
    );
    return sortStreams(multiStreams, state.sortBy);
  },
  allInOneStreamsRaw: (state) =>
    state.streams.filter((stream) => stream.type === "live"),
  allInOneStreams: (state) => {
    const allInOneStream = state.streams.filter(
      (stream) => stream.type === "live",
    );
    return sortStreams(allInOneStream, state.sortBy);
  },
  subSecondStreams: (state) => {
    const subSecondStreams = state.streams.filter(
      (stream) => stream.type === "subsecond",
    );
    return sortStreams(subSecondStreams, state.sortBy);
  },
  vodStreams: (state) =>
    state.streams.filter((stream) => stream.type === "vod"),
  vodStream: (state) =>
    state.streams.find(
      (stream) => stream._id === router.currentRoute.params?.streamId,
    ),
  routerId: (state) => {
    return router.currentRoute.params?.streamId;
  },
  realVodStreams: (state) =>
    state.streams.filter(
      (stream) => stream.type === "vod" && !stream.liveParent,
    ),
  vodFiles: (state) => {
    const lowerCaseStreams = state.vodFiles.map((item) => ({
      ...item,
      lowerCaseName: _.lowerCase(item.fileName),
    }));
    return sortStreams(lowerCaseStreams, state.sortBy, true);
  },
  vodSubfolders: (state) => state.vodSubfolders,
  vodFilesFolderId: (state) => state.vodFilesFolderId,
  scheduledStreams: (state) => {
    const scheduledStreams = state.streams.filter(
      (stream) => stream.type === "scheduled",
    );
    return sortStreams(scheduledStreams, state.sortBy);
  },
  hasVodFiles: (state, getters) => {
    let videoFilesNumber = 0;
    getters.vodStreams.map(
      (stream) => (videoFilesNumber = videoFilesNumber + stream.totalFiles),
    );
    if (videoFilesNumber > 0) {
      return true;
    } else return false;
  },
  tvPlayouts: (state) => {
    const playouts = state.streams.filter(
      (stream) => stream.type === "tvplayout",
    );
    return sortStreams(playouts, state.sortBy);
  },
  tvPlayout: (state) => {
    return state.streams.find(
      (stream) => stream._id === router.currentRoute.value.params?.tvId,
    );
  },
  totalVodBytes: (state, getters) => {
    const sum = getters.vodStreams.reduce(
      (accum, item) => accum + item.totalBytes,
      0,
    );
    return sum / 1000 ** 3;
  },
  totalRealVodBytes: (state, getters) => {
    const sum = getters.realVodStreams.reduce(
      (accum, item) => accum + item.totalBytes,
      0,
    );
    return sum / 1000 ** 3;
  },
  totalVodFiles: (state, getters) => {
    const sum = getters.realVodStreams.reduce(
      (accum, item) => accum + item.totalFiles,
      0,
    );
    return sum;
  },
  sortBy: (state) => state.sortBy,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
